import EditIcon from '@mui/icons-material/Edit';
import { Avatar, Box, Grid, Stack, Typography, styled, IconButton } from '@mui/material';
import { useEffect, useState } from 'react';
import { IJob, IWorkAssignmentDTO } from 'wisestep-api';
import {
  ScrollableTabs,
  TabTextVariant,
  timeSince,
  WsColors,
  WsDrawer,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
  IUserDTO,
} from 'wisestep-base';

import { useUserContext } from '../../../../../../context';
import { checkPermissionForJobAdminTeamOrCreatedUser } from '../../../../../../features/jobs/utils/JobListSideNavHelper';
import ActiveRecruiters from '../activeRecruiters';
import EditJobAdminTeam from '../editJobAdminTeam';
import EditWorkAssignmentTeam from '../editWorkAssignmentTeam';
import Recruiters from '../recruiters/Recruiters';
import Teams from '../teams/Teams';

const AvatarTheme = styled(Avatar)(() => ({
  height: 32,
  width: 32,
}));
interface asigneesProps {
  job: IJob;
  workAssignment?: IWorkAssignmentDTO;
  refreshJob(): void;
  refreshWorkAssignmentTeam(): void;
  openJobAdminTeam?: boolean;
  openWorkAssignmentTeam?: boolean;
}

const Assignees = ({
  job,
  workAssignment,
  refreshJob,
  refreshWorkAssignmentTeam,
  openJobAdminTeam = false,
  openWorkAssignmentTeam = false,
}: asigneesProps) => {
  const [value, setValue] = useState(0);
  const [openJobAdminTeamEdit, setOpenJobAdminTeamEdit] = useState(openJobAdminTeam);
  const [openWorkAssignmentEdit, setOpenWorkAssignmentEdit] = useState(openWorkAssignmentTeam);
  const [editJobAdminTeamAllowed, setEditJobAdminTeamAllowed] = useState<boolean>(false);

  const tabsList = [
    {
      tabName: 'Recruiters',
    },
    {
      tabName: 'Teams',
    },
    {
      tabName: 'Active Recruiters',
    },
  ];

  const loggedInUser = useUserContext().userDTO as IUserDTO;

  useEffect(() => {
    const fetchData = async () => {
      setEditJobAdminTeamAllowed(
        await checkPermissionForJobAdminTeamOrCreatedUser(job, loggedInUser)
      );
    };
    fetchData();
  }, [job, loggedInUser]);

  const closeWorkAssignmentDrawer = () => setOpenWorkAssignmentEdit(false);
  const closeJobAdminTeamEdit = () => setOpenJobAdminTeamEdit(false);
  return (
    <>
      <Box>
        <Box
          sx={{
            borderRadius: 2,
            backgroundColor: WsColors.TheBlues900,
          }}
        >
          <Stack direction="row" p={4}>
            <Grid item container xs={4}>
              <Box>
                <Box pb={0.5}>
                  <WsTypography variant={WsTypoVariants.BODY2} color={WsTypoColors.SECONDARY}>
                    Posted by
                  </WsTypography>
                </Box>
                <Stack direction="row">
                  <AvatarTheme alt={job.jobOwner} src="/images/Ahmed.png" />
                  <Stack direction="column" spacing={0.5}>
                    <Box pl={2} pt={0.5}>
                      {job.jobOwner && (
                        <Typography color={WsColors.White} fontWeight="bold" fontSize="16px">
                          {job.jobOwner}
                        </Typography>
                      )}
                    </Box>
                    <Box pl={2} pt={0.3}>
                      {job.createdOn && (
                        <WsTypography variant={WsTypoVariants.BODY2} color={WsTypoColors.SECONDARY}>
                          {timeSince(String(job.createdOn))}
                        </WsTypography>
                      )}
                    </Box>
                  </Stack>
                </Stack>
              </Box>
            </Grid>
            <Grid item xs={4} container>
              {job.team?.name && (
                <Box>
                  <Box pb={0.5}>
                    <WsTypography variant={WsTypoVariants.BODY2} color={WsTypoColors.SECONDARY}>
                      Job Admin Team
                    </WsTypography>
                  </Box>
                  <Stack direction="row">
                    <AvatarTheme alt={job.team.name} src="/images/Ahmed.png" />
                    <Box pl={2} pt={0.5}>
                      <Stack direction="row" spacing={0.5}>
                        <Typography color={WsColors.White} fontSize="16px">
                          {job.team.name}
                        </Typography>
                        {editJobAdminTeamAllowed && (
                          <IconButton
                            color="secondary"
                            sx={{ p: 0, mt: -1.5 }}
                            onClick={() => setOpenJobAdminTeamEdit(true)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Stack>
                    </Box>
                  </Stack>
                </Box>
              )}
            </Grid>
            {workAssignment?.team?.name && (
              <Grid item xs={4} container>
                <Box>
                  <Box pb={0.5}>
                    <WsTypography variant={WsTypoVariants.BODY2} color={WsTypoColors.SECONDARY}>
                      Work Assignment Team
                    </WsTypography>
                  </Box>
                  <Stack direction="row">
                    <AvatarTheme alt={workAssignment.team.name} src="/images/Ahmed.png" />
                    <Box pl={2} pt={0.5}>
                      <Stack direction="row" spacing={0.5}>
                        <Typography color={WsColors.White} fontSize="16px">
                          {workAssignment.team.name}
                        </Typography>
                        <IconButton
                          color="secondary"
                          sx={{ p: 0, mt: -1.5 }}
                          onClick={() => setOpenWorkAssignmentEdit(true)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Box>
                  </Stack>
                </Box>
              </Grid>
            )}
          </Stack>
        </Box>
        <Box>
          <ScrollableTabs
            scrollList={tabsList}
            getTabIndex={(index: number) => setValue(index)}
            variant={TabTextVariant.SUB_TITLE}
          />
        </Box>
        {value == 0 && <Recruiters job={job} onRecruiterChange={refreshJob} />}
        {value == 1 && job.id && <Teams jobId={job.id} />}
        {value == 2 && <ActiveRecruiters job={job} onRecruiterChange={refreshJob} />}
      </Box>
      <WsDrawer
        size="small"
        open={openJobAdminTeamEdit}
        onClose={closeJobAdminTeamEdit}
        title="Edit Job Admin Team"
        style={{ zIndex: 1300 }}
        headerSize="small"
      >
        <EditJobAdminTeam
          job={job}
          closeDrawer={closeJobAdminTeamEdit}
          onJobAdminTeamChange={refreshJob}
        />
      </WsDrawer>
      <WsDrawer
        size="small"
        open={openWorkAssignmentEdit}
        onClose={closeWorkAssignmentDrawer}
        title="Edit Work Assignment Team"
        style={{ zIndex: 1300 }}
        headerSize="small"
      >
        {workAssignment && (
          <EditWorkAssignmentTeam
            workAssignment={workAssignment}
            closeDrawer={closeWorkAssignmentDrawer}
            onWorkAssignmentTeamChange={refreshWorkAssignmentTeam}
          />
        )}
      </WsDrawer>
    </>
  );
};

export default Assignees;
